import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** The translator as react-i18next hands it over, so a helper can take it without widening its type. */
type Translate = ReturnType<typeof useTranslation>["t"];
import { formatDate, formatDateTime } from "../../lib/format";
import { complaintsApi, type ComplaintStaffOption, type CorrectiveAction } from "../../api/complaints";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";
import { Select } from "../Select";
import Ic from "../icons.jsx";

/**
 * Corrective measures on a complaint. The workshop of 11.09.2026 made them mandatory on an accepted
 * complaint, and the complaint does not close until every one of them is carried out — which is why the
 * panel says out loud how many are still open.
 *
 * <p>Carrying a measure out and checking that it worked are two acts and two people: the portal refuses
 * a check signed by whoever carried the measure out, because a person confirming their own work
 * confirms nothing.
 */
export function ComplaintCorrectiveActions({
  complaintId,
  canManage,
  onChanged,
}: {
  complaintId: string;
  canManage: boolean;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { canFull } = useSectionAccess("complaint_processing");
  const [actions, setActions] = useState<CorrectiveAction[] | null>(null);
  const [staff, setStaff] = useState<ComplaintStaffOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [description, setDescription] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [noteFor, setNoteFor] = useState<{ id: string; mode: "complete" | "effectiveness" } | null>(null);
  const [note, setNote] = useState("");

  async function load() {
    try {
      setActions(await complaintsApi.listCorrectiveActions(complaintId));
    } catch {
      setActions([]);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the loader is stable per complaint
  }, [complaintId]);

  useEffect(() => {
    if (!canManage) return;
    complaintsApi.staff().then(setStaff).catch(() => setStaff([]));
  }, [canManage]);

  const outstanding = (actions ?? []).filter((a) => a.completedAt === null).length;

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    try {
      await work();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errorText(t, e as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    if (!description.trim()) return;
    await run(async () => {
      await complaintsApi.createCorrectiveAction(complaintId, {
        description: description.trim(),
        rootCause: rootCause.trim() || null,
        ownerUserId: ownerUserId || null,
        dueOn: dueOn || null,
      });
      setDescription("");
      setRootCause("");
      setOwnerUserId("");
      setDueOn("");
      setAdding(false);
    });
  }

  async function submitNote() {
    if (!noteFor || !note.trim() || (noteFor.mode === "effectiveness" && !canFull)) return;
    const { id, mode } = noteFor;
    await run(async () => {
      if (mode === "complete") {
        await complaintsApi.completeCorrectiveAction(complaintId, id, note.trim());
      } else {
        await complaintsApi.checkCorrectiveActionEffectiveness(complaintId, id, note.trim());
      }
      setNoteFor(null);
      setNote("");
    });
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>{t("complaints.measures.title", "Korektivne mere")}</h2>
        {outstanding > 0 ? (
          <span className="tag warn">
            {t("complaints.measures.outstanding", "{{n}} nesprovedeno", { n: outstanding })}
          </span>
        ) : (
          (actions ?? []).length > 0 && <span className="tag ok">{t("complaints.measures.allDone", "sve sprovedene")}</span>
        )}
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p className="sub" style={{ margin: 0 }}>
          {t(
            "complaints.measures.hint",
            "Osnovana reklamacija se ne zatvara dok mera nije sprovedena. Efikasnost potpisuje druga osoba, ne ona koja je meru sprovela.",
          )}
        </p>

        {(actions ?? []).length === 0 ? (
          <div className="empty">{t("complaints.measures.empty", "Nema upisanih mera.")}</div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {(actions ?? []).map((a) => (
              <li
                key={a.id}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r-md)",
                  padding: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <strong style={{ color: "var(--ink)" }}>{a.description}</strong>
                  {a.completedAt === null ? (
                    <span className="tag warn">{t("complaints.measures.open", "u toku")}</span>
                  ) : a.effectivenessCheckedAt === null ? (
                    <span className="tag neutral">{t("complaints.measures.awaitingCheck", "čeka proveru")}</span>
                  ) : (
                    <span className="tag ok">{t("complaints.measures.verified", "provereno")}</span>
                  )}
                </div>
                {a.rootCause && (
                  <div className="muted">
                    {t("complaints.measures.rootCause", "Uzrok")}: {a.rootCause}
                  </div>
                )}
                <div className="muted">
                  {a.ownerName && `${t("complaints.measures.owner", "Nosilac")}: ${a.ownerName} · `}
                  {a.dueOn && `${t("complaints.measures.due", "Rok")}: ${formatDate(a.dueOn)} · `}
                  {t("complaints.measures.createdBy", "Upisao")}: {a.createdByName ?? "—"}
                </div>
                {a.completedAt && (
                  <div className="muted">
                    {t("complaints.measures.completed", "Sprovedeno")}: {formatDateTime(a.completedAt)}
                    {a.completedByName ? ` · ${a.completedByName}` : ""}
                    {a.evidenceNote ? ` · ${a.evidenceNote}` : ""}
                  </div>
                )}
                {a.effectivenessCheckedAt && (
                  <div className="muted">
                    {t("complaints.measures.checked", "Efikasnost")}: {formatDateTime(a.effectivenessCheckedAt)}
                    {a.effectivenessCheckedByName ? ` · ${a.effectivenessCheckedByName}` : ""}
                    {a.effectivenessNote ? ` · ${a.effectivenessNote}` : ""}
                  </div>
                )}

                {canManage && noteFor?.id === a.id && (noteFor.mode !== "effectiveness" || canFull) ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <textarea
                      className="input"
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
                      placeholder={
                        noteFor.mode === "complete"
                          ? t("complaints.measures.evidencePlaceholder", "Čime se dokazuje da je mera sprovedena?")
                          : t("complaints.measures.checkPlaceholder", "Šta pokazuje da mera radi?")
                      }
                    />
                    <div className="toolbar">
                      <button type="button" className="btn sm" onClick={() => { setNoteFor(null); setNote(""); }}>
                        {t("action.cancel", "Odustani")}
                      </button>
                      <button
                        type="button"
                        className="btn primary sm"
                        disabled={busy || !note.trim()}
                        onClick={() => void submitNote()}
                      >
                        {t("complaints.measures.save", "Upiši")}
                      </button>
                    </div>
                  </div>
                ) : (
                  canManage && (
                    <div className="toolbar">
                      {a.completedAt === null && (
                        <button
                          type="button"
                          className="btn sm"
                          disabled={busy}
                          onClick={() => { setNoteFor({ id: a.id, mode: "complete" }); setNote(""); }}
                        >
                          <Ic.check /> {t("complaints.measures.markDone", "Sprovedeno")}
                        </button>
                      )}
                      {canFull && a.completedAt !== null && a.effectivenessCheckedAt === null && (
                        <button
                          type="button"
                          className="btn sm"
                          disabled={busy}
                          onClick={() => { setNoteFor({ id: a.id, mode: "effectiveness" }); setNote(""); }}
                        >
                          {t("complaints.measures.checkEffectiveness", "Proveri efikasnost")}
                        </button>
                      )}
                    </div>
                  )
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage &&
          (adding ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("complaints.measures.descriptionPlaceholder", "Šta se menja")}
              />
              <input
                className="input"
                value={rootCause}
                onChange={(e) => setRootCause(e.target.value)}
                placeholder={t("complaints.measures.rootCausePlaceholder", "Zašto je došlo do odstupanja")}
              />
              <div className="toolbar">
                <Select<string>
                  value={ownerUserId}
                  onChange={setOwnerUserId}
                  ariaLabel={t("complaints.measures.owner", "Nosilac")}
                  style={{ minWidth: 200 }}
                  options={[
                    { value: "", label: t("complaints.measures.noOwner", "Bez nosioca") },
                    ...staff.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                />
                <input
                  className="input"
                  type="date"
                  value={dueOn}
                  onChange={(e) => setDueOn(e.target.value)}
                  aria-label={t("complaints.measures.due", "Rok")}
                />
                <button type="button" className="btn sm" onClick={() => setAdding(false)}>
                  {t("action.cancel", "Odustani")}
                </button>
                <button
                  type="button"
                  className="btn primary sm"
                  disabled={busy || !description.trim()}
                  onClick={() => void create()}
                >
                  {t("complaints.measures.add", "Dodaj meru")}
                </button>
              </div>
            </div>
          ) : (
            <div className="toolbar">
              <button type="button" className="btn sm" onClick={() => setAdding(true)}>
                <Ic.plus /> {t("complaints.measures.add", "Dodaj meru")}
              </button>
            </div>
          ))}
      </div>
    </section>
  );
}

function errorText(t: Translate, err: ApiError): string {
  switch (err.code) {
    case "EFFECTIVENESS_SAME_PERSON":
      return t(
        "complaints.error.effectivenessSamePerson",
        "Efikasnost potpisuje druga osoba, ne ona koja je meru sprovela.",
      );
    case "CORRECTIVE_ACTION_NOT_COMPLETED":
      return t("complaints.error.measureNotCompleted", "Mera još nije sprovedena, pa se ne može proveriti.");
    case "CORRECTIVE_ACTION_COMPLETED":
      return t("complaints.error.measureCompleted", "Sprovedena mera se više ne menja.");
    case "EFFECTIVENESS_ALREADY_CHECKED":
      return t("complaints.error.effectivenessChecked", "Efikasnost je već proverena.");
    case "COMPLAINT_CLOSED":
      return t("complaints.error.complaintClosed", "Zatvorena reklamacija ne prima nove mere.");
    default:
      return err.message;
  }
}
